import React, { useEffect, useState, useRef } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { Textarea } from '../components/ui/Textarea';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { ProgressiveNumberInput } from '../components/ProgressiveNumberInput';
import { Tooltip } from '../components/ui/Tooltip';
import { Plus, Filter, Download, Eye, Edit, Search, HardHat, FileText, GraduationCap, Stethoscope, Trash2, Calendar, AlertTriangle, Upload, CheckCircle2, X, ExternalLink, Save, AlertCircle, MapPin, Building2, File } from 'lucide-react';
import { useRBAC } from '../contexts/RBACContext';
import { PermissionGate } from '../components/PermissionGate';
import { MultiSelect } from '../components/ui/MultiSelect';
import { mockData } from '../services/mockData';
import { motion, AnimatePresence } from 'framer-motion';
import { DatePicker } from '../components/ui/DatePicker';
const initialWorkersData = mockData.getWorkers();
const columnsConfig = [{
  key: 'name',
  label: 'Lavoratore'
}, {
  key: 'company',
  label: 'Azienda'
}, {
  key: 'role',
  label: 'Mansione'
}, {
  key: 'trainingStatus',
  label: 'Formazione'
}, {
  key: 'healthStatus',
  label: 'Sorv. sanitaria'
}, {
  key: 'nextExpiry',
  label: 'Prox. scadenza'
}, {
  key: 'actions',
  label: 'Azioni'
}];
const safetyRolesList = [{
  value: 'rls',
  label: 'RLS'
}, {
  value: 'preposto',
  label: 'Preposto'
}, {
  value: 'antincendio',
  label: 'Addetto antincendio'
}, {
  value: 'primosoccorso',
  label: 'Addetto primo soccorso'
}, {
  value: 'dirigente',
  label: 'Dirigente per la sicurezza'
}, {
  value: 'rspp',
  label: 'RSPP'
}, {
  value: 'aspp',
  label: 'ASPP'
}, {
  value: 'altro',
  label: 'Altro'
}];
const sediOptions = [{
  value: 'Sede principale',
  label: 'Sede principale'
}, {
  value: 'Sede operativa',
  label: 'Sede operativa'
}, {
  value: 'Filiale Nord',
  label: 'Filiale Nord'
}, {
  value: 'Filiale Sud',
  label: 'Filiale Sud'
}, {
  value: 'Stabilimento 1',
  label: 'Stabilimento 1'
}, {
  value: 'Stabilimento 2',
  label: 'Stabilimento 2'
}];
const initialContractTypes = [{
  value: 'Determinato',
  label: 'Determinato'
}, {
  value: 'Indeterminato',
  label: 'Indeterminato'
}, {
  value: 'Part-time',
  label: 'Part-time'
}, {
  value: 'Collaborazione',
  label: 'Collaborazione'
}, {
  value: 'Stage',
  label: 'Stage'
}, {
  value: 'Altro',
  label: 'Altro'
}];
export function WorkersPage() {
  const {
    canCreate,
    canEdit,
    canDelete
  } = useRBAC();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [activeTab, setActiveTab] = useState<'info' | 'training' | 'health'>('info');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importStep, setImportStep] = useState<'upload' | 'preview' | 'success'>('upload');
  const [importData, setImportData] = useState<any[]>([]);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Custom Contract Type State
  const [contractTypes, setContractTypes] = useState(initialContractTypes);
  const [showCustomContractInput, setShowCustomContractInput] = useState(false);
  const [newContractType, setNewContractType] = useState('');
  // Form state for new worker
  const [formData, setFormData] = useState<any>({});
  // Inline add rows for detail modal
  const [newVisits, setNewVisits] = useState<any[]>([]);
  const [newCourses, setNewCourses] = useState<any[]>([]);
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(initialWorkersData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['name', 'company', 'fiscalCode']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setFormData(item);
    setIsModalOpen(true);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    setFormData({
      code: '',
      nextVisitDate: '',
      notes: ''
    });
    setIsModalOpen(true);
  };
  const handleViewDetail = (item: any) => {
    setSelectedItem(item);
    setActiveTab('info');
    setNewVisits([]);
    setNewCourses([]);
    setIsDetailOpen(true);
  };
  const handleDetailToEdit = () => {
    setIsDetailOpen(false);
    handleEdit(selectedItem);
  };
  const handleDelete = (item: any) => {
    setItemToDelete(item);
    setShowDeleteDialog(true);
  };
  const confirmDelete = () => {
    setDeleteSuccess(true);
    setTimeout(() => {
      setDeleteSuccess(false);
      setShowDeleteDialog(false);
      setItemToDelete(null);
    }, 1500);
  };
  const handleSave = () => {
    console.log('Saving worker:', formData);
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
    }, 1500);
  };
  const handleAddCustomContractType = () => {
    if (newContractType.trim()) {
      const newType = {
        value: newContractType,
        label: newContractType
      };
      setContractTypes([...contractTypes, newType]);
      setFormData({
        ...formData,
        contractType: newContractType
      });
      setNewContractType('');
      setShowCustomContractInput(false);
    } else {
      setShowCustomContractInput(false);
      setFormData({
        ...formData,
        contractType: ''
      });
    }
  };
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const mockParsedData = [{
        name: 'Mario Rossi',
        company: 'Azienda 1 SRL',
        role: 'Operaio',
        fiscalCode: 'RSSMRA80A01A123B'
      }, {
        name: 'Laura Bianchi',
        company: 'Azienda 1 SRL',
        role: 'Impiegata',
        fiscalCode: 'BNCLRA85B02B456C'
      }, {
        name: 'Giuseppe Verdi',
        company: 'Azienda 2 SRL',
        role: 'Tecnico',
        fiscalCode: 'VRDGPP78C03C789D'
      }];
      setImportData(mockParsedData);
      setImportStep('preview');
    }
  };
  const handleConfirmImport = () => {
    console.log('Importing workers:', importData);
    setImportStep('success');
    setTimeout(() => {
      setShowImportModal(false);
      setImportStep('upload');
      setImportData([]);
    }, 2000);
  };
  const handleCourseFileUpload = (idx: number) => {
    // Simulate file upload
    const updated = [...newCourses];
    updated[idx] = {
      ...updated[idx],
      documentName: 'attestato_pregresso.pdf'
    };
    setNewCourses(updated);
  };
  const mockCourses = [{
    id: 1,
    name: 'Sicurezza generale',
    date: '12/10/2020',
    expiry: '12/10/2025',
    status: 'valid',
    certificateUrl: '#'
  }, {
    id: 2,
    name: 'Primo soccorso',
    date: '01/01/2021',
    expiry: '01/01/2024',
    status: 'expired',
    certificateUrl: '#'
  }, {
    id: 3,
    name: 'Antincendio rischio medio',
    date: '15/03/2023',
    expiry: '15/03/2026',
    status: 'valid',
    certificateUrl: '#'
  }];
  return <Layout data-id="element-5237">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-5238">
        <div data-id="element-5239">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-5240">Lavoratori</h1>
          <p className="text-slate-500 mt-1" data-id="element-5241">
            Gestione dipendenti, formazione e sorveglianza sanitaria.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-5242">
          <Button variant="flat" color="secondary" startContent={<Upload className="h-4 w-4" data-id="element-5244" />} onClick={() => setShowImportModal(true)} data-id="element-5243">
            Import massivo
          </Button>
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-5246" />} data-id="element-5245">
            Esporta
          </Button>
          <PermissionGate module="workers" action="create" data-id="element-5247">
            <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-5249" />} data-id="element-5248">
              Nuovo lavoratore
            </Button>
          </PermissionGate>
        </div>
      </div>

      <Card className="mb-6" noPadding data-id="element-5250">
        <div className="p-4 flex flex-col gap-4" data-id="element-5251">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-5252">
            <div className="flex-1" data-id="element-5253">
              <Input placeholder="Cerca lavoratore, azienda, CF..." startContent={<Search className="h-4 w-4" data-id="element-5255" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-5254" />
            </div>
            <div className="flex gap-2" data-id="element-5256">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-5258" />} data-id="element-5257">
                Filtri{' '}
                {Object.keys(filters).length > 0 && <Badge variant="primary" size="sm" className="ml-1" data-id="element-5259">
                    {Object.keys(filters).length}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="workers" lockedColumns={['name']} data-id="element-5260" />
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-5261">
              <Select label="Stato formazione" value={filters.trainingStatus || ''} onChange={e => setFilter('trainingStatus', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'valid',
            label: 'In regola'
          }, {
            value: 'expiring',
            label: 'In scadenza'
          }, {
            value: 'expired',
            label: 'Scaduto'
          }]} data-id="element-5262" />
              <Select label="Sorveglianza" value={filters.healthStatus || ''} onChange={e => setFilter('healthStatus', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'fit',
            label: 'Idoneo'
          }, {
            value: 'unfit',
            label: 'Non idoneo'
          }]} data-id="element-5263" />
              <div className="col-span-full flex justify-end" data-id="element-5264">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-5266" />} data-id="element-5265">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Card noPadding className="overflow-hidden" data-id="element-5267">
        <div className="overflow-x-auto" data-id="element-5268">
          <table className="w-full text-left border-collapse table-auto" data-id="element-5269">
            <thead data-id="element-5270">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-5271">
                {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-4 py-4 ${col.key === 'actions' ? 'text-left pr-6' : ''}`} data-id="element-5272">
                      {col.label}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-5273">
              {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-5274">
                  {visibleColumns.includes('name') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-5275">
                      {item.name}
                    </td>}
                  {visibleColumns.includes('company') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-5276">
                      {item.company}
                    </td>}
                  {visibleColumns.includes('role') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-5277">
                      {item.role}
                    </td>}
                  {visibleColumns.includes('trainingStatus') && <td className="px-4 py-4" data-id="element-5278">
                      <Badge variant={item.trainingStatus === 'valid' || item.trainingStatus === 'In regola' ? 'success' : item.trainingStatus === 'expiring' || item.trainingStatus === 'In scadenza' ? 'warning' : 'danger'} data-id="element-5279">
                        {item.trainingStatus === 'valid' || item.trainingStatus === 'In regola' ? 'In regola' : item.trainingStatus === 'expiring' || item.trainingStatus === 'In scadenza' ? 'In scadenza' : 'Scaduto'}
                      </Badge>
                    </td>}
                  {visibleColumns.includes('healthStatus') && <td className="px-4 py-4" data-id="element-5280">
                      <Badge variant={item.healthStatus === 'fit' || item.healthStatus === 'Idoneo' ? 'success' : item.healthStatus === 'Parzialmente idoneo' ? 'warning' : 'danger'} data-id="element-5281">
                        {item.healthStatus === 'fit' || item.healthStatus === 'Idoneo' ? 'Idoneo' : item.healthStatus === 'Parzialmente idoneo' ? 'Parzialmente idoneo' : 'Non idoneo'}
                      </Badge>
                    </td>}
                  {visibleColumns.includes('nextExpiry') && <td className="px-4 py-4 text-sm font-mono text-slate-600" data-id="element-5282">
                      {item.nextExpiry}
                    </td>}
                  {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-5283">
                      <div className="flex items-center gap-1" data-id="element-5284">
                        <Tooltip content="Visualizza lavoratore" data-id="element-5285">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleViewDetail(item)} data-id="element-5286">
                            <Eye className="h-4 w-4 text-slate-500" data-id="element-5287" />
                          </Button>
                        </Tooltip>
                        <PermissionGate module="workers" action="edit" data-id="element-5288">
                          <Tooltip content="Modifica lavoratore" data-id="element-5289">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleEdit(item)} data-id="element-5290">
                              <Edit className="h-4 w-4 text-seform-orange" data-id="element-5291" />
                            </Button>
                          </Tooltip>
                        </PermissionGate>
                        <PermissionGate module="workers" action="delete" data-id="element-5292">
                          <Tooltip content="Elimina lavoratore" data-id="element-5293">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleDelete(item)} data-id="element-5294">
                              <Trash2 className="h-4 w-4 text-red-500" data-id="element-5295" />
                            </Button>
                          </Tooltip>
                        </PermissionGate>
                      </div>
                    </td>}
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100" data-id="element-5296">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-5297" />
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? 'Modifica lavoratore' : 'Nuovo lavoratore'} size="2xl" data-id="element-5298">
        <AnimatePresence mode="wait" data-id="element-5299">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-5300">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-5301">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-5302" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-5303">
                Lavoratore salvato con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-5304">
              <div className="space-y-8" data-id="element-5305">
                <section data-id="element-5306">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-5307">
                    <HardHat className="h-4 w-4" data-id="element-5308" /> Dati personali
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5309">
                    <Input label="Nome e cognome" placeholder="Mario Rossi" value={formData.name || ''} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} required data-id="element-5310" />
                    <ProgressiveNumberInput type="worker" value={formData.code} onChange={val => setFormData({
                  ...formData,
                  code: val
                })} disabled={false} data-id="element-5311" />
                    <Input label="Codice fiscale" placeholder="RSSMRA..." value={formData.fiscalCode || ''} onChange={e => setFormData({
                  ...formData,
                  fiscalCode: e.target.value
                })} required data-id="element-5312" />
                    <Input label="Data di nascita" type="date" value={formData.birthDate || ''} onChange={e => setFormData({
                  ...formData,
                  birthDate: e.target.value
                })} data-id="element-5313" />
                    <Input label="Luogo di nascita" placeholder="Città" value={formData.birthPlace || ''} onChange={e => setFormData({
                  ...formData,
                  birthPlace: e.target.value
                })} data-id="element-5314" />
                    <Input label="Provincia" placeholder="Es. AR, FI, RM..." value={formData.birthProvince || ''} onChange={e => setFormData({
                  ...formData,
                  birthProvince: e.target.value
                })} data-id="element-5315" />
                    <Input label="Nazionalità" placeholder="Es. Italiana" value={formData.nationality || ''} onChange={e => setFormData({
                  ...formData,
                  nationality: e.target.value
                })} data-id="element-5316" />
                    <Input label="Email personale" type="email" placeholder="mario.rossi@email.it" value={formData.email || ''} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} required description="Fondamentale per invio attestati" data-id="element-5317" />
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-5318"></div>

                <section data-id="element-5319">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-5320">
                    <FileText className="h-4 w-4" data-id="element-5321" /> Rapporto di lavoro
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5322">
                    <Select label="Azienda" value={formData.company || ''} onChange={e => setFormData({
                  ...formData,
                  company: e.target.value
                })} options={[{
                  value: 'Azienda 0 SRL',
                  label: 'Azienda 0 SRL'
                }, {
                  value: 'Studio 1 Associati',
                  label: 'Studio 1 Associati'
                }, {
                  value: 'Impresa 2 SPA',
                  label: 'Impresa 2 SPA'
                }, {
                  value: 'Azienda 3 SRL',
                  label: 'Azienda 3 SRL'
                }, {
                  value: 'Studio 4 Associati',
                  label: 'Studio 4 Associati'
                }, {
                  value: 'Impresa 5 SPA',
                  label: 'Impresa 5 SPA'
                }]} required data-id="element-5323" />
                    <Input label="Mansione" value={formData.role || ''} onChange={e => setFormData({
                  ...formData,
                  role: e.target.value
                })} data-id="element-5324" />
                    <Input label="Reparto di lavoro" placeholder="Es. Produzione, Logistica..." value={formData.department || ''} onChange={e => setFormData({
                  ...formData,
                  department: e.target.value
                })} data-id="element-5325" />
                    <Select label="Sede" value={formData.sede || ''} onChange={e => setFormData({
                  ...formData,
                  sede: e.target.value
                })} options={[{
                  value: '',
                  label: 'Seleziona sede...'
                }, ...sediOptions]} data-id="element-5326" />
                    <Select label="Tipo di rapporto" value={formData.employmentType || ''} onChange={e => setFormData({
                  ...formData,
                  employmentType: e.target.value
                })} options={[{
                  value: '',
                  label: 'Seleziona...'
                }, {
                  value: 'Dipendente',
                  label: 'Dipendente'
                }, {
                  value: 'Amministratore',
                  label: 'Amministratore'
                }, {
                  value: 'Dirigente',
                  label: 'Dirigente'
                }, {
                  value: 'Collaboratore',
                  label: 'Collaboratore'
                }, {
                  value: 'Altro',
                  label: 'Altro'
                }]} data-id="element-5327" />

                    {/* Custom Contract Type Select */}
                    <div data-id="element-5328">
                      <Select label="Tipo di contratto" value={formData.contractType || ''} onChange={e => {
                    if (e.target.value === '__custom__') {
                      setShowCustomContractInput(true);
                    } else {
                      setFormData({
                        ...formData,
                        contractType: e.target.value
                      });
                      setShowCustomContractInput(false);
                    }
                  }} options={[{
                    value: '',
                    label: 'Seleziona...'
                  }, ...contractTypes, {
                    value: '__custom__',
                    label: '+ Aggiungi nuovo tipo...'
                  }]} data-id="element-5329" />
                      {showCustomContractInput && <div className="mt-2 animate-in fade-in slide-in-from-top-2" data-id="element-5330">
                          <Input autoFocus placeholder="Inserisci nuovo tipo di contratto" value={newContractType} onChange={e => setNewContractType(e.target.value)} onBlur={handleAddCustomContractType} onKeyDown={e => e.key === 'Enter' && handleAddCustomContractType()} data-id="element-5331" />
                        </div>}
                    </div>

                    <div className="col-span-full" data-id="element-5332">
                      <label className="block text-sm font-medium text-slate-700 mb-3" data-id="element-5333">
                        Ruoli sicurezza
                      </label>
                      <div className="grid grid-cols-2 gap-3" data-id="element-5334">
                        {safetyRolesList.map(role => <label key={role.value} className="flex items-center gap-2 cursor-pointer" data-id="element-5335">
                            <input type="checkbox" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={(formData.safetyRoles || []).includes(role.value)} onChange={e => {
                        const current = formData.safetyRoles || [];
                        if (e.target.checked) {
                          setFormData({
                            ...formData,
                            safetyRoles: [...current, role.value]
                          });
                        } else {
                          setFormData({
                            ...formData,
                            safetyRoles: current.filter((r: string) => r !== role.value)
                          });
                        }
                      }} data-id="element-5336" />
                            <span className="text-sm text-slate-700" data-id="element-5337">
                              {role.label}
                            </span>
                          </label>)}
                      </div>
                    </div>
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-5338"></div>

                <section data-id="element-5339">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-5340">
                    <Stethoscope className="h-4 w-4" data-id="element-5341" /> Sorveglianza sanitaria
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5342">
                    <Input label="Data prossima visita" type="date" value={formData.nextVisitDate || ''} onChange={e => setFormData({
                  ...formData,
                  nextVisitDate: e.target.value
                })} data-id="element-5343" />
                    <div className="col-span-full" data-id="element-5344">
                      <Textarea label="Note" placeholder="Eventuali note sulla sorveglianza sanitaria..." value={formData.notes || ''} onChange={e => setFormData({
                    ...formData,
                    notes: e.target.value
                  })} data-id="element-5345" />
                    </div>
                  </div>
                </section>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-5346">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-5347">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-5349" />} data-id="element-5348">
                    Salva lavoratore
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Detail Modal */}
      <Modal isOpen={isDetailOpen} onClose={() => setIsDetailOpen(false)} title="Scheda lavoratore" size="2xl" data-id="element-5350">
        {selectedItem && <div className="flex flex-col h-[70vh]" data-id="element-5351">
            {/* Header */}
            <div className="flex items-start gap-4 mb-6" data-id="element-5352">
              <div data-id="element-5353">
                <h2 className="text-2xl font-bold text-seform-navy" data-id="element-5354">
                  {selectedItem.name}
                </h2>
                <p className="text-slate-500" data-id="element-5355">
                  {selectedItem.role} presso{' '}
                  <span className="font-semibold text-seform-orange" data-id="element-5356">
                    {selectedItem.company}
                  </span>
                </p>
                <div className="flex gap-2 mt-2" data-id="element-5357">
                  <Badge variant={selectedItem.trainingStatus === 'valid' || selectedItem.trainingStatus === 'In regola' ? 'success' : selectedItem.trainingStatus === 'expiring' || selectedItem.trainingStatus === 'In scadenza' ? 'warning' : 'danger'} data-id="element-5358">
                    Formazione:{' '}
                    {selectedItem.trainingStatus === 'valid' || selectedItem.trainingStatus === 'In regola' ? 'OK' : selectedItem.trainingStatus === 'In scadenza' || selectedItem.trainingStatus === 'expiring' ? 'In scadenza' : 'Scaduta'}
                  </Badge>
                  <Badge variant={selectedItem.healthStatus === 'fit' || selectedItem.healthStatus === 'Idoneo' ? 'success' : 'danger'} data-id="element-5359">
                    Visita:{' '}
                    {selectedItem.healthStatus === 'fit' || selectedItem.healthStatus === 'Idoneo' ? 'Idoneo' : 'Non idoneo'}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-slate-200 mb-6" data-id="element-5360">
              <button className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'info' ? 'border-seform-orange text-seform-orange' : 'border-transparent text-slate-500 hover:text-slate-700'}`} onClick={() => setActiveTab('info')} data-id="element-5361">
                Anagrafica
              </button>
              <button className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'training' ? 'border-seform-orange text-seform-orange' : 'border-transparent text-slate-500 hover:text-slate-700'}`} onClick={() => setActiveTab('training')} data-id="element-5362">
                Formazione
              </button>
              <button className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'health' ? 'border-seform-orange text-seform-orange' : 'border-transparent text-slate-500 hover:text-slate-700'}`} onClick={() => setActiveTab('health')} data-id="element-5363">
                Sorveglianza sanitaria
              </button>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto custom-scrollbar pt-1 px-1" data-id="element-5364">
              {activeTab === 'info' && <div className="space-y-8" data-id="element-5365">
                  <section data-id="element-5366">
                    <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-5367">
                      <HardHat className="h-4 w-4" data-id="element-5368" /> Dati personali
                    </h4>
                    <div className="grid grid-cols-2 gap-6" data-id="element-5369">
                      <div className="space-y-1" data-id="element-5370">
                        <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5371">
                          Nome e cognome
                        </span>
                        <p className="font-medium" data-id="element-5372">{selectedItem.name}</p>
                      </div>
                      <div className="space-y-1" data-id="element-5373">
                        <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5374">
                          Codice
                        </span>
                        <p className="font-mono font-medium" data-id="element-5375">
                          {selectedItem.code || '-'}
                        </p>
                      </div>
                      <div className="space-y-1" data-id="element-5376">
                        <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5377">
                          Codice fiscale
                        </span>
                        <p className="font-mono font-medium" data-id="element-5378">
                          {selectedItem.fiscalCode || '-'}
                        </p>
                      </div>
                      <div className="space-y-1" data-id="element-5379">
                        <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5380">
                          Data di nascita
                        </span>
                        <p className="font-medium" data-id="element-5381">
                          {selectedItem.birthDate || '-'}
                        </p>
                      </div>
                      <div className="space-y-1" data-id="element-5382">
                        <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5383">
                          Luogo di nascita
                        </span>
                        <p className="font-medium" data-id="element-5384">
                          {selectedItem.birthPlace || '-'}
                        </p>
                      </div>
                      <div className="space-y-1" data-id="element-5385">
                        <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5386">
                          Provincia
                        </span>
                        <p className="font-medium" data-id="element-5387">
                          {selectedItem.birthProvince || '-'}
                        </p>
                      </div>
                      <div className="space-y-1" data-id="element-5388">
                        <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5389">
                          Nazionalità
                        </span>
                        <p className="font-medium" data-id="element-5390">
                          {selectedItem.nationality || '-'}
                        </p>
                      </div>
                      <div className="space-y-1" data-id="element-5391">
                        <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5392">
                          Email
                        </span>
                        <p className="font-medium" data-id="element-5393">
                          {selectedItem.email || '-'}
                        </p>
                      </div>
                    </div>
                  </section>

                  <div className="h-px bg-slate-100" data-id="element-5394"></div>

                  <section data-id="element-5395">
                    <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-5396">
                      <FileText className="h-4 w-4" data-id="element-5397" /> Rapporto di lavoro
                    </h4>
                    <div className="grid grid-cols-2 gap-6" data-id="element-5398">
                      <div className="space-y-1" data-id="element-5399">
                        <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5400">
                          Azienda
                        </span>
                        <p className="font-medium" data-id="element-5401">
                          {selectedItem.company || '-'}
                        </p>
                      </div>
                      <div className="space-y-1" data-id="element-5402">
                        <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5403">
                          Mansione
                        </span>
                        <p className="font-medium" data-id="element-5404">
                          {selectedItem.role || '-'}
                        </p>
                      </div>
                      <div className="space-y-1" data-id="element-5405">
                        <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5406">
                          Reparto di lavoro
                        </span>
                        <p className="font-medium" data-id="element-5407">
                          {selectedItem.department || '-'}
                        </p>
                      </div>
                      <div className="space-y-1" data-id="element-5408">
                        <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5409">
                          Sede
                        </span>
                        <p className="font-medium" data-id="element-5410">
                          {selectedItem.sede || '-'}
                        </p>
                      </div>
                      <div className="space-y-1" data-id="element-5411">
                        <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5412">
                          Tipo di rapporto
                        </span>
                        <p className="font-medium" data-id="element-5413">
                          {selectedItem.employmentType || '-'}
                        </p>
                      </div>
                      <div className="space-y-1" data-id="element-5414">
                        <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5415">
                          Tipo di contratto
                        </span>
                        <p className="font-medium" data-id="element-5416">
                          {selectedItem.contractType || '-'}
                        </p>
                      </div>
                      <div className="space-y-1 col-span-full" data-id="element-5417">
                        <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5418">
                          Ruoli sicurezza
                        </span>
                        <div className="flex gap-1 flex-wrap mt-1" data-id="element-5419">
                          {selectedItem.safetyRoles && selectedItem.safetyRoles.length > 0 ? selectedItem.safetyRoles.map((role: string) => {
                      const roleLabel = safetyRolesList.find(r => r.value === role)?.label || role;
                      return <Badge key={role} size="sm" data-id="element-5420">
                                  {roleLabel}
                                </Badge>;
                    }) : <span className="text-sm text-slate-400" data-id="element-5421">
                              Nessun ruolo assegnato
                            </span>}
                        </div>
                      </div>
                    </div>
                  </section>
                </div>}

              {activeTab === 'training' && <div className="space-y-4" data-id="element-5422">
                  <div className="flex justify-between items-center sticky top-0 bg-white z-10 py-1 pr-1" data-id="element-5423">
                    <h4 className="font-bold text-seform-navy" data-id="element-5424">
                      Corsi effettuati
                    </h4>
                    <Button size="sm" startContent={<Plus className="h-4 w-4" data-id="element-5426" />} onClick={() => setNewCourses([...newCourses, {
                id: Date.now(),
                name: '',
                date: '',
                expiry: '',
                status: 'valid',
                documentName: ''
              }])} data-id="element-5425">
                      Aggiungi corso
                    </Button>
                  </div>
                  <div className="border border-slate-100 rounded-xl overflow-hidden" data-id="element-5427">
                    <table className="w-full text-sm text-left" data-id="element-5428">
                      <thead className="bg-slate-50 text-slate-500 font-semibold" data-id="element-5429">
                        <tr data-id="element-5430">
                          <th className="p-3" data-id="element-5431">Corso</th>
                          <th className="p-3" data-id="element-5432">Data</th>
                          <th className="p-3" data-id="element-5433">Scadenza</th>
                          <th className="p-3" data-id="element-5434">Stato</th>
                          <th className="p-3" data-id="element-5435">Attestato</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100" data-id="element-5436">
                        {mockCourses.map(course => <tr key={course.id} data-id="element-5437">
                            <td className="p-3 font-medium" data-id="element-5438">{course.name}</td>
                            <td className="p-3" data-id="element-5439">{course.date}</td>
                            <td className="p-3" data-id="element-5440">{course.expiry}</td>
                            <td className="p-3" data-id="element-5441">
                              <Badge variant={course.status === 'valid' ? 'success' : 'danger'} size="sm" data-id="element-5442">
                                {course.status === 'valid' ? 'Valido' : 'Scaduto'}
                              </Badge>
                            </td>
                            <td className="p-3" data-id="element-5443">
                              <Button variant="ghost" size="sm" className="!px-2 text-seform-orange" data-id="element-5444">
                                <ExternalLink className="h-4 w-4" data-id="element-5445" />
                                <span className="ml-1" data-id="element-5446">Vedi</span>
                              </Button>
                            </td>
                          </tr>)}
                        {newCourses.map((course, idx) => <tr key={`new-course-${course.id}`} className="bg-orange-50/30" data-id="element-5447">
                            <td className="p-2" data-id="element-5448">
                              <Select value={course.name} onChange={e => {
                        const updated = [...newCourses];
                        updated[idx] = {
                          ...updated[idx],
                          name: e.target.value
                        };
                        setNewCourses(updated);
                      }} options={[{
                        value: '',
                        label: 'Seleziona corso...'
                      }, {
                        value: 'Pregresso',
                        label: 'Pregresso'
                      }, {
                        value: 'Sicurezza generale',
                        label: 'Sicurezza generale'
                      }, {
                        value: 'Primo soccorso',
                        label: 'Primo soccorso'
                      }, {
                        value: 'Antincendio rischio medio',
                        label: 'Antincendio rischio medio'
                      }, {
                        value: 'Antincendio rischio basso',
                        label: 'Antincendio rischio basso'
                      }, {
                        value: 'Preposto',
                        label: 'Preposto'
                      }, {
                        value: 'RLS',
                        label: 'RLS'
                      }, {
                        value: 'Lavori in quota',
                        label: 'Lavori in quota'
                      }, {
                        value: 'HACCP',
                        label: 'HACCP'
                      }]} data-id="element-5449" />
                            </td>
                            <td className="p-2" data-id="element-5450">
                              <DatePicker value={course.date} onChange={e => {
                        const updated = [...newCourses];
                        updated[idx] = {
                          ...updated[idx],
                          date: e.target.value
                        };
                        setNewCourses(updated);
                      }} data-id="element-5451" />
                            </td>
                            <td className="p-2" data-id="element-5452">
                              <DatePicker value={course.expiry} onChange={e => {
                        const updated = [...newCourses];
                        updated[idx] = {
                          ...updated[idx],
                          expiry: e.target.value
                        };
                        setNewCourses(updated);
                      }} data-id="element-5453" />
                            </td>
                            <td className="p-2" data-id="element-5454">
                              <Select value={course.status} onChange={e => {
                        const updated = [...newCourses];
                        updated[idx] = {
                          ...updated[idx],
                          status: e.target.value
                        };
                        setNewCourses(updated);
                      }} options={[{
                        value: 'valid',
                        label: 'Valido'
                      }, {
                        value: 'expired',
                        label: 'Scaduto'
                      }]} data-id="element-5455" />
                            </td>
                            <td className="p-2" data-id="element-5456">
                              <div className="flex gap-1 items-center" data-id="element-5457">
                                {course.name === 'Pregresso' && <Tooltip content="Carica documento" data-id="element-5458">
                                    <Button size="sm" variant="flat" color="secondary" className="!px-2" onClick={() => handleCourseFileUpload(idx)} data-id="element-5459">
                                      <Upload className="h-3.5 w-3.5" data-id="element-5460" />
                                    </Button>
                                  </Tooltip>}
                                {course.documentName && <Tooltip content={`Scarica ${course.documentName}`} data-id="element-5461">
                                    <Button size="sm" variant="ghost" className="!px-2 text-blue-600" onClick={() => alert(`Downloading ${course.documentName}`)} data-id="element-5462">
                                      <FileText className="h-4 w-4" data-id="element-5463" />
                                    </Button>
                                  </Tooltip>}
                                <Button size="sm" variant="flat" color="success" className="!px-2" onClick={() => {}} data-id="element-5464">
                                  <Save className="h-3.5 w-3.5" data-id="element-5465" />
                                </Button>
                                <Button size="sm" variant="flat" color="danger" className="!px-2" onClick={() => setNewCourses(newCourses.filter((_, i) => i !== idx))} data-id="element-5466">
                                  <X className="h-3.5 w-3.5" data-id="element-5467" />
                                </Button>
                              </div>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>}

              {activeTab === 'health' && <div className="space-y-4" data-id="element-5468">
                  <div className="flex justify-between items-center sticky top-0 bg-white z-10 py-1 pr-1" data-id="element-5469">
                    <h4 className="font-bold text-seform-navy" data-id="element-5470">
                      Visite mediche
                    </h4>
                    <Button size="sm" startContent={<Plus className="h-4 w-4" data-id="element-5472" />} onClick={() => setNewVisits([...newVisits, {
                id: Date.now(),
                type: '',
                date: '',
                outcome: '',
                expiry: ''
              }])} data-id="element-5471">
                      Aggiungi visita
                    </Button>
                  </div>
                  <div className="border border-slate-100 rounded-xl overflow-hidden" data-id="element-5473">
                    <table className="w-full text-sm text-left" data-id="element-5474">
                      <thead className="bg-slate-50 text-slate-500 font-semibold" data-id="element-5475">
                        <tr data-id="element-5476">
                          <th className="p-3" data-id="element-5477">Tipo visita</th>
                          <th className="p-3" data-id="element-5478">Data</th>
                          <th className="p-3" data-id="element-5479">Esito</th>
                          <th className="p-3" data-id="element-5480">Anni validità</th>
                          <th className="p-3" data-id="element-5481">Scadenza</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100" data-id="element-5482">
                        <tr data-id="element-5483">
                          <td className="p-3" data-id="element-5484">Periodica</td>
                          <td className="p-3" data-id="element-5485">10/01/2024</td>
                          <td className="p-3" data-id="element-5486">
                            <Badge variant="success" size="sm" data-id="element-5487">
                              Idoneo
                            </Badge>
                          </td>
                          <td className="p-3" data-id="element-5488">2</td>
                          <td className="p-3" data-id="element-5489">10/01/2026</td>
                        </tr>
                        {newVisits.map((visit, idx) => <tr key={`new-visit-${visit.id}`} className="bg-orange-50/30" data-id="element-5490">
                            <td className="p-2" data-id="element-5491">
                              <Select value={visit.type} onChange={e => {
                        const updated = [...newVisits];
                        updated[idx] = {
                          ...updated[idx],
                          type: e.target.value
                        };
                        setNewVisits(updated);
                      }} options={[{
                        value: '',
                        label: 'Seleziona...'
                      }, {
                        value: 'Preventiva',
                        label: 'Preventiva'
                      }, {
                        value: 'Periodica',
                        label: 'Periodica'
                      }, {
                        value: 'Straordinaria',
                        label: 'Straordinaria'
                      }, {
                        value: 'Preassuntiva',
                        label: 'Preassuntiva'
                      }, {
                        value: 'Cambio mansione',
                        label: 'Cambio mansione'
                      }]} data-id="element-5492" />
                            </td>
                            <td className="p-2" data-id="element-5493">
                              <DatePicker value={visit.date} onChange={e => {
                        const updated = [...newVisits];
                        updated[idx] = {
                          ...updated[idx],
                          date: e.target.value
                        };
                        setNewVisits(updated);
                      }} data-id="element-5494" />
                            </td>
                            <td className="p-2" data-id="element-5495">
                              <Select value={visit.outcome} onChange={e => {
                        const updated = [...newVisits];
                        updated[idx] = {
                          ...updated[idx],
                          outcome: e.target.value
                        };
                        setNewVisits(updated);
                      }} options={[{
                        value: '',
                        label: 'Seleziona...'
                      }, {
                        value: 'Idoneo',
                        label: 'Idoneo'
                      }, {
                        value: 'Parzialmente idoneo',
                        label: 'Parzialmente idoneo'
                      }, {
                        value: 'Non idoneo',
                        label: 'Non idoneo'
                      }]} data-id="element-5496" />
                            </td>
                            <td className="p-2" data-id="element-5497">
                              <Input type="number" placeholder="2" value={visit.validityYears || ''} onChange={e => {
                        const updated = [...newVisits];
                        updated[idx] = {
                          ...updated[idx],
                          validityYears: e.target.value
                        };
                        setNewVisits(updated);
                      }} data-id="element-5498" />
                            </td>
                            <td className="p-2" data-id="element-5499">
                              <div className="flex gap-1 items-center" data-id="element-5500">
                                <DatePicker value={visit.expiry} onChange={e => {
                          const updated = [...newVisits];
                          updated[idx] = {
                            ...updated[idx],
                            expiry: e.target.value
                          };
                          setNewVisits(updated);
                        }} data-id="element-5501" />
                                <Button size="sm" variant="flat" color="success" className="!px-2 flex-shrink-0" onClick={() => {}} data-id="element-5502">
                                  <Save className="h-3.5 w-3.5" data-id="element-5503" />
                                </Button>
                                <Button size="sm" variant="flat" color="danger" className="!px-2 flex-shrink-0" onClick={() => setNewVisits(newVisits.filter((_, i) => i !== idx))} data-id="element-5504">
                                  <X className="h-3.5 w-3.5" data-id="element-5505" />
                                </Button>
                              </div>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>}
            </div>

            {/* Footer Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 mt-auto" data-id="element-5506">
              <Button variant="flat" color="default" onClick={() => setIsDetailOpen(false)} data-id="element-5507">
                Chiudi
              </Button>
              <Button onClick={handleDetailToEdit} startContent={<Edit className="h-4 w-4" data-id="element-5509" />} data-id="element-5508">
                Modifica
              </Button>
            </div>
          </div>}
      </Modal>

      {/* Import Modal */}
      <Modal isOpen={showImportModal} onClose={() => {
      setShowImportModal(false);
      setImportStep('upload');
      setImportData([]);
    }} title="Import massivo lavoratori" size="lg" data-id="element-5510">
        <AnimatePresence mode="wait" data-id="element-5511">
          {importStep === 'upload' && <motion.div key="upload" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="space-y-6" data-id="element-5512">
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center" data-id="element-5513">
                <Upload className="h-12 w-12 text-slate-300 mx-auto mb-4" data-id="element-5514" />
                <p className="text-slate-600 mb-2" data-id="element-5515">
                  Trascina qui il file CSV o clicca per selezionarlo
                </p>
                <p className="text-sm text-slate-400 mb-4" data-id="element-5516">
                  Formato supportato: CSV con colonne Nome, Azienda, Mansione,
                  CF
                </p>
                <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleFileUpload} data-id="element-5517" />
                <Button onClick={() => fileInputRef.current?.click()} startContent={<Upload className="h-4 w-4" data-id="element-5519" />} data-id="element-5518">
                  Seleziona file
                </Button>
              </div>
            </motion.div>}

          {importStep === 'preview' && <motion.div key="preview" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="space-y-6" data-id="element-5520">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2" data-id="element-5521">
                <CheckCircle2 className="h-5 w-5 text-green-600" data-id="element-5522" />
                <span className="text-green-700 font-medium" data-id="element-5523">
                  {importData.length} lavoratori trovati nel file
                </span>
              </div>
              <div className="border border-slate-100 rounded-xl overflow-hidden max-h-64 overflow-y-auto" data-id="element-5524">
                <table className="w-full text-sm text-left" data-id="element-5525">
                  <thead className="bg-slate-50 text-slate-500 font-semibold sticky top-0" data-id="element-5526">
                    <tr data-id="element-5527">
                      <th className="p-3" data-id="element-5528">Nome</th>
                      <th className="p-3" data-id="element-5529">Azienda</th>
                      <th className="p-3" data-id="element-5530">Mansione</th>
                      <th className="p-3" data-id="element-5531">CF</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-5532">
                    {importData.map((row, i) => <tr key={i} data-id="element-5533">
                        <td className="p-3 font-medium" data-id="element-5534">{row.name}</td>
                        <td className="p-3" data-id="element-5535">{row.company}</td>
                        <td className="p-3" data-id="element-5536">{row.role}</td>
                        <td className="p-3 font-mono text-xs" data-id="element-5537">
                          {row.fiscalCode}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-3" data-id="element-5538">
                <Button variant="flat" color="default" onClick={() => setImportStep('upload')} data-id="element-5539">
                  Indietro
                </Button>
                <Button onClick={handleConfirmImport} startContent={<CheckCircle2 className="h-4 w-4" data-id="element-5541" />} data-id="element-5540">
                  Conferma import
                </Button>
              </div>
            </motion.div>}

          {importStep === 'success' && <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-12" data-id="element-5542">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-5543">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-5544" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-5545">
                Import completato con successo
              </p>
              <p className="text-sm text-slate-500 mt-1" data-id="element-5546">
                {importData.length} lavoratori importati
              </p>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} title="Conferma eliminazione" size="sm" data-id="element-5547">
        <AnimatePresence mode="wait" data-id="element-5548">
          {deleteSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-12" data-id="element-5549">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-5550">
                <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-5551" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-5552">
                Eliminato con successo
              </p>
            </motion.div> : <motion.div key="confirm" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-5553">
              <div className="space-y-6" data-id="element-5554">
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-5555">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-5556" />
                  <div data-id="element-5557">
                    <p className="text-sm text-red-700 font-medium" data-id="element-5558">
                      Attenzione: questa azione è irreversibile.
                    </p>
                    <p className="text-sm text-red-600 mt-1" data-id="element-5559">
                      Il lavoratore <strong data-id="element-5560">{itemToDelete?.name}</strong> verrà
                      eliminato definitivamente.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-5561">
                  <Button variant="flat" color="default" onClick={() => setShowDeleteDialog(false)} data-id="element-5562">
                    Annulla
                  </Button>
                  <Button color="danger" onClick={confirmDelete} startContent={<Trash2 className="h-4 w-4" data-id="element-5564" />} data-id="element-5563">
                    Elimina
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}